import { jsxs, jsx } from "react/jsx-runtime";
import { useThemeName, Box, Text, Bleed, ButtonIcon, IconClear } from "braid-design-system";
import { useMultipleSelection, useCombobox } from "downshift";
import { useRef } from "react";
import { gap, inputParent, noClearIconSpace, themeBasePaddingYStandard, themeBasePaddingYSmall, flex, hidden, smallText, standardText, input, suggestionList } from "./styles.css.mjs";
const MultiselectAutosuggest = ({
  id,
  size = "standard",
  label,
  suggestions,
  selectedItems,
  clearLabel,
  placeholder,
  searchText,
  hideLabel = false,
  dropdownBoxProps = {
    background: "surface",
    borderRadius: "standard",
    width: "full",
    zIndex: "dropdown",
    boxShadow: "medium",
    position: "absolute"
  },
  setSearchText,
  onClickSuggestionItem,
  onItemsSelected,
  renderSelectedItems,
  renderSuggestions,
  onMenuClose,
  isItemDisabled
}) => {
  const inputRef = useRef(null);
  const { getSelectedItemProps, getDropdownProps } = useMultipleSelection({
    selectedItems,
    onStateChange: ({ type, selectedItems: newSelectedItems }) => {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          onItemsSelected(newSelectedItems ?? []);
          break;
      }
    }
  });
  const {
    isOpen,
    highlightedIndex,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    openMenu
  } = useCombobox({
    items: suggestions,
    itemToString: (item) => item ? item.id : "",
    inputValue: searchText,
    isItemDisabled: isItemDisabled ?? (() => false),
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          const newChanges = {
            ...changes,
            isOpen: true,
            // keeps the menu open after selection.
            highlightedIndex: state.highlightedIndex
          };
          return newChanges;
        case useCombobox.stateChangeTypes.InputClick:
          return {
            ...changes,
            isOpen: true
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          if (onMenuClose) {
            onMenuClose();
          }
          if (selectedItem) {
            onClickSuggestionItem(selectedItem);
          }
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          if (onMenuClose) {
            onMenuClose();
          }
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            onClickSuggestionItem(selectedItem);
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setSearchText(newInputValue ?? "");
          break;
      }
    }
  });
  const hasSelectedItems = selectedItems.length > 0;
  const shouldShowClearIcon = searchText.length > 0;
  const themeName = useThemeName();
  return /* @__PURE__ */ jsxs(
    Box,
    {
      id: `${id}-multiselect-autosuggest`,
      display: "flex",
      flexDirection: "column",
      position: "relative",
      width: "full",
      className: gap[size],
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            display: hideLabel ? "none" : "block",
            component: "label",
            ...getLabelProps({
              onClick: () => {
                openMenu();
              }
            }),
            children: /* @__PURE__ */ jsx(Text, { size, weight: "strong", children: label })
          }
        ),
        /* @__PURE__ */ jsxs(
          Box,
          {
            position: "relative",
            background: "surface",
            width: "full",
            onClick: (e) => {
              openMenu();
              e.stopPropagation();
            },
            className: {
              [inputParent]: true,
              [noClearIconSpace]: shouldShowClearIcon,
              [themeBasePaddingYStandard[themeName === "seekJobs" ? "seekJobs" : "apac"]]: size === "standard",
              [themeBasePaddingYSmall[themeName === "seekJobs" ? "seekJobs" : "apac"]]: size === "small"
            },
            children: [
              renderSelectedItems ? renderSelectedItems({ getSelectedItemProps }) : null,
              /* @__PURE__ */ jsx(
                "input",
                {
                  className: `${isOpen || searchText || !searchText && selectedItems.length === 0 ? flex : hidden} ${size === "small" ? smallText : standardText} ${input}`,
                  placeholder: hasSelectedItems ? "" : placeholder,
                  ...getInputProps(
                    getDropdownProps({ ref: inputRef, value: searchText }),
                    {
                      suppressRefError: true
                    }
                  )
                }
              ),
              shouldShowClearIcon ? /* @__PURE__ */ jsx(
                Box,
                {
                  component: "span",
                  position: "absolute",
                  width: "touchable",
                  height: "touchable",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  top: 0,
                  right: 0,
                  children: /* @__PURE__ */ jsx(Bleed, { vertical: "xxsmall", children: /* @__PURE__ */ jsx(
                    ButtonIcon,
                    {
                      id: `${id}-clear-multiselect-autosuggest-input`,
                      label: clearLabel || "Clear",
                      variant: "transparent",
                      tone: "neutral",
                      type: "reset",
                      onClick: () => setSearchText(""),
                      icon: /* @__PURE__ */ jsx(IconClear, {})
                    }
                  ) })
                }
              ) : null
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          Box,
          {
            component: "ul",
            display: isOpen ? "block" : "none",
            ...dropdownBoxProps,
            className: suggestionList,
            ...getMenuProps({}, { suppressRefError: true }),
            children: renderSuggestions({ highlightedIndex, getItemProps })
          }
        )
      ]
    }
  );
};
export {
  MultiselectAutosuggest
};
